import {put, call} from 'redux-saga/effects';
import InvoiceBatch from "../../entities/invoiceBatch";
import * as Actions from "../actions/invoiceBatchActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type InvoiceBatchAction = {
    invoiceBatchId: string;
    invoiceBatch: InvoiceBatch;
    invoiceBatches: InvoiceBatch[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneInvoiceBatch(action: InvoiceBatchAction) {
	const session = tabSession.captureGeneration();
	try {
		const invoiceBatch: InvoiceBatch = yield call(api.findOne, 'invoiceBatch', action.invoiceBatchId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_FETCH_ONE_SUCCESS, invoiceBatchId: action.invoiceBatchId, invoiceBatch});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCH_FETCH_ONE_FAILURE, invoiceBatchId: action.invoiceBatchId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {InvoiceBatchAction} action
 */
export function* saveInvoiceBatch(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ INVOICEBATCH_SAVE saga', action.invoiceBatch?.id);
    
	if (action.invoiceBatch === undefined || action.invoiceBatch === null) {
		console.error('saveInvoiceBatch received a null');
		throw new Error('saveInvoiceBatch received a null');
	}

	if (!(action.invoiceBatch instanceof InvoiceBatch)) {
		action.invoiceBatch = new InvoiceBatch(action.invoiceBatch);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoiceBatch', action.invoiceBatch);
		if (addResult.isErr()) {
			console.error(`⮂ saveInvoiceBatch: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.INVOICEBATCH_SAVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_SAVE, invoiceBatch: action.invoiceBatch});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.INVOICEBATCH_SAVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {InvoiceBatchAction} action
 * @param {InvoiceBatch[]} action.invoiceBatches
 */
export function* saveManyInvoiceBatches(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ INVOICEBATCH_SAVE_MANY saga', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));
    
    if (!Array.isArray(action.invoiceBatches)) {
        console.error('saveManyInvoiceBatches expected an array, received', action.invoiceBatches);
        throw new Error('saveManyInvoiceBatches expected an array');
    }

	try {
		const toSave: InvoiceBatch[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.invoiceBatches.length; i++) {
			const invoiceBatch = action.invoiceBatches[i];
			invoiceBatch.persisted = false;
			toSave.push(invoiceBatch);
		}

		const addResult = yield call(idb.add, 'invoiceBatch', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyInvoiceBatches: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.INVOICEBATCH_SAVE_MANY_FAILURE, invoiceBatches: action.invoiceBatches, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_SAVE_MANY, invoiceBatches: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.INVOICEBATCH_SAVE_MANY_FAILURE, invoiceBatches: action.invoiceBatches, status: 500, err});

		console.error(`Error saving ${action.invoiceBatches.length} invoiceBatches to database`, err);
	}
}

/**
 * @param {InvoiceBatchAction} action
 */
export function* removeInvoiceBatch(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.invoiceBatch === undefined || action.invoiceBatch === null || !(action.invoiceBatch instanceof InvoiceBatch)) {
			console.error('removeInvoiceBatch expected type InvoiceBatch, received', action.invoiceBatch);
			throw new Error('removeInvoiceBatch expected type InvoiceBatch');
		}

	try {
		// Mark as deleted
		action.invoiceBatch.status = 'D';
		action.invoiceBatch.addedOn = newHRTimestamp();
		action.invoiceBatch.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoiceBatch', action.invoiceBatch);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeInvoiceBatch: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCH_REMOVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_REMOVE, invoiceBatch: action.invoiceBatch});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCH_REMOVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this InvoiceBatch.
	*
	* @param {InvoiceBatchAction} action
	*/
export function* restoreInvoiceBatch(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreInvoiceBatch expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.invoiceBatch === undefined || action.invoiceBatch === null) {
        console.error('restoreInvoiceBatch expected a InvoiceBatch, received null');
        throw new Error('restoreInvoiceBatch expected a InvoiceBatch, received null');
    }
    else if (!(action.invoiceBatch instanceof InvoiceBatch)) action.invoiceBatch = new InvoiceBatch(action.invoiceBatch); 
    
	try {
		// Mark as restored
		action.invoiceBatch.status = 'U';
		action.invoiceBatch.addedOn = newHRTimestamp();
		action.invoiceBatch.addedBy = userId;
		action.invoiceBatch.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoiceBatch', action.invoiceBatch);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreInvoiceBatch: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCH_RESTORE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_RESTORE, invoiceBatch: action.invoiceBatch});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCH_RESTORE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, err});

		console.error('Error restoring from database', err);
	}
}